import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useComputedLanguage } from "../hooks/useComputedLanguage";
import { Pokemon } from "../misc/data-types";
import { formatMonsterNumber } from "../misc/formatMonsterNumber";
import { getWikiLink, getWikiName } from "../misc/wiki";
import { Badge } from "./Badge";
import { FancyLink } from "./FancyLink";
import { FancyText } from "./FancyText";
import { Flex } from "./Flex";
import {
  IconCry,
  IconDefenseSolo,
  IconDefenseTeam,
  IconGender,
  IconOffenseDual,
  IconOffenseSingle,
  IconShiny,
} from "./icons";
import { IconButton } from "./IconButton";
import styles from "./Monster.module.css";
import { MonsterImage } from "./MonsterImage";
import { StatsTable } from "./StatsTable";
import { Gender } from "../misc/getImage";

export type MonsterProps = {
  pokemon: Pokemon;
  setQuery: (query: string) => void;
};

export function Monster({ pokemon, setQuery }: MonsterProps): ReactNode {
  const audioRef = useRef<HTMLAudioElement>(null);
  const { t, i18n } = useTranslation();
  const language = useComputedLanguage();
  const [shiny, setShiny] = useState(false);
  const [gender, setGender] = useState<Gender>("default");
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationState, setAnimationState] = useState<0 | 1 | 2>(0);
  const displayNumber = formatMonsterNumber(pokemon.number);
  const params = new URLSearchParams({
    types: pokemon.types.join(" "),
    ability: "none",
    tera_type: "none",
  });
  const teamParams = new URLSearchParams({
    add_types: pokemon.types.join(" "),
  });
  const speciesName = pokemon.speciesNames[language] || pokemon.speciesNames.en;
  const formName = pokemon.formNames[language] || pokemon.formNames.en;
  const idPrefix = `pokemon-${pokemon.id}`;
  const monsterParams = new URLSearchParams({ q: String(pokemon.number) });
  function animate() {
    setAnimationState(animationState === 1 ? 2 : 1);
  }
  return (
    <div className={styles.root}>
      <Flex direction="column" className={styles.name}>
        <Flex align="center" gap="medium" wrap>
          <FancyText
            tag="h2"
            id={`${idPrefix}-name`}
            fontSize="xlarge"
            fontWeight="medium"
            className={styles.title}
          >
            {speciesName}
          </FancyText>
          <div className={styles.buttonContainer}>
            <audio
              ref={audioRef}
              preload="none"
              aria-hidden="true"
              hidden={true}
              autoPlay={false}
              onPlay={() => {
                setIsPlaying(true);
                animate();
                if (audioRef.current) {
                  audioRef.current.volume = 20 / 100;
                }
              }}
              onEnded={() => {
                setIsPlaying(false);
                // Deferred: batching this with setIsPlaying in the same commit
                // forces the sprite's CSS animation to restart at the same
                // moment the button's border-radius transition needs to start,
                // and the transition loses out. A separate tick keeps them from
                // colliding.
                requestAnimationFrame(animate);
              }}
              onError={() => {
                setIsPlaying(false);
                requestAnimationFrame(animate);
              }}
            >
              <source src={`/cry/${pokemon.id}.ogg`} type="audio/ogg" />
              <source src={`/cry/${pokemon.id}.m4a`} type="audio/mp4" />
              <source src={`/cry/${pokemon.id}.aac`} type="audio/aac" />
            </audio>
            <IconButton
              title={t("pokedex.cry.text")}
              aria-label={t("pokedex.cry.text")}
              aria-pressed={isPlaying}
              disabled={!pokemon.hasCry}
              onClick={() => {
                const audio = audioRef.current;
                if (!audio) {
                  return;
                }
                if (audio.paused) {
                  audio.play();
                }
              }}
            >
              <IconCry size={24} />
            </IconButton>
            <IconButton
              title={t("pokedex.gender.text")}
              aria-label={t("pokedex.gender.text")}
              aria-pressed={gender !== "default"}
              disabled={!pokemon.images.female}
              onClick={() => {
                setGender((g) => {
                  if (g === "default") {
                    return "female";
                  }
                  return "default";
                });
                animate();
              }}
            >
              <IconGender size={24} />
            </IconButton>
            <IconButton
              title={t("pokedex.shiny.text")}
              aria-label={t("pokedex.shiny.text")}
              aria-pressed={shiny}
              disabled={!pokemon.images.shiny}
              onClick={() => {
                setShiny(!shiny);
                animate();
              }}
            >
              <IconShiny size={24} />
            </IconButton>
          </div>
        </Flex>
        <Flex gap="medium">
          <FancyText tag="div" tabularNums>
            <FancyLink
              to={{ search: monsterParams.toString() }}
              underline="never"
              onClick={(event) => {
                event.preventDefault();
                setQuery(String(pokemon.number));
              }}
            >
              {displayNumber}
            </FancyLink>
          </FancyText>
          <FancyText tag="div" id={`${idPrefix}-form`}>
            {formName}
          </FancyText>
        </Flex>
      </Flex>
      <div className={styles.monster}>
        <div className={styles.monsterIcon}>
          <Flex direction="column">
            <Flex paddingY="large" justify="center">
              <MonsterImage
                pokemonID={pokemon.id}
                shiny={shiny}
                animationState={animationState}
                gender={gender}
              />
            </Flex>
            <Flex
              wrap
              gap="small"
              justify="flex-start"
              align="flex-start"
              className={styles.badges}
            >
              {pokemon.types.map((t, i) => (
                <Badge key={i} type={t} size="small" />
              ))}
            </Flex>
          </Flex>
        </div>
        <div className={styles.monsterLinks}>
          <FancyLink
            iconOnly
            outlined
            aria-labelledby={`${idPrefix}-offense ${idPrefix}-name ${idPrefix}-form`}
            to={`/offense/single/?${params}#matchup-offense`}
            id={`${idPrefix}-offense`}
          >
            <IconOffenseSingle
              size={24}
              aria-label={t("offense.mode.single")}
            />
          </FancyLink>
          <FancyLink
            iconOnly
            outlined
            aria-labelledby={`${idPrefix}-offense ${idPrefix}-name ${idPrefix}-form`}
            to={`/offense/dual/?${params}#matchup-offense`}
            id={`${idPrefix}-offense`}
          >
            <IconOffenseDual
              size={24}
              aria-label={t("offense.mode.combination")}
            />
          </FancyLink>
          <FancyLink
            iconOnly
            outlined
            aria-labelledby={`${idPrefix}-defense ${idPrefix}-name ${idPrefix}-form`}
            to={`/defense/solo/?${params}#matchup-defense`}
            id={`${idPrefix}-defense`}
          >
            <IconDefenseSolo size={24} aria-label={t("defense.mode.solo")} />
          </FancyLink>
          <FancyLink
            iconOnly
            outlined
            aria-labelledby={`${idPrefix}-defense-team ${idPrefix}-name ${idPrefix}-form`}
            to={`/defense/team/?${teamParams}`}
            id={`${idPrefix}-defense-team`}
          >
            <IconDefenseTeam size={24} aria-label={t("defense.mode.team")} />
          </FancyLink>
          <Flex flex="auto" />
          <FancyLink
            outlined
            aria-labelledby={`${idPrefix}-wiki ${idPrefix}-name ${idPrefix}-form`}
            to={getWikiLink(i18n.language, pokemon)}
            id={`${idPrefix}-wiki`}
          >
            {getWikiName(i18n.language)}
          </FancyLink>
        </div>
        <div className={styles.monsterStats}>
          <StatsTable pokemon={pokemon} />
        </div>
      </div>
    </div>
  );
}
